/**
 * API调用钩子
 * 提供统一的API调用、加载状态和错误处理
 */

import { useState, useCallback, useRef } from 'react';
import { ApiState } from '../services/api/types';

// 使用统一的ApiState类型

// API调用选项
export interface UseApiOptions {
  immediate?: boolean;
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
}

/**
 * API调用钩子
 */
export function useApi<T = any>(
  apiFunction: (...args: any[]) => Promise<T>,
  options: UseApiOptions = {}
) {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(async (...args: any[]) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const result = await apiFunction(...args);
      setState({ data: result, loading: false, error: null });
      
      if (options.onSuccess) {
        options.onSuccess(result);
      }
      
      return result;
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : String(error) || '请求失败';
      setState({ data: null, loading: false, error: errorMessage });
      
      if (options.onError) {
        options.onError(errorMessage);
      }
      
      throw error;
    }
  }, [apiFunction, options]);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return {
    ...state,
    execute,
    reset,
  };
}

/**
 * 分页API调用钩子
 */
export function usePaginationApi<T = any>(
  apiFunction: (params: any) => Promise<{ data: { rows: T[]; total: number } }>,
  initialParams: any = {}
) {
  const [state, setState] = useState<ApiState<{ rows: T[]; total: number }>>({
    data: { rows: [], total: 0 },
    loading: false,
    error: null,
  });

  const [params, setParams] = useState(initialParams);

  const execute = useCallback(async (newParams?: any) => {
    const requestParams = { ...params, ...newParams };
    setParams(requestParams);
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const result = await apiFunction(requestParams);
      setState({ 
        data: result.data, 
        loading: false, 
        error: null 
      });
      return result;
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : String(error) || '请求失败';
      setState({ 
        data: { rows: [], total: 0 }, 
        loading: false, 
        error: errorMessage 
      });
      throw error;
    }
  }, [apiFunction, params]);

  const reset = useCallback(() => {
    setState({ data: { rows: [], total: 0 }, loading: false, error: null });
    setParams(initialParams);
  }, [initialParams]);

  return {
    ...state,
    execute,
    reset,
    params,
    setParams,
  };
}

/**
 * 列表API调用钩子
 */
export function useListApi<T = any>(
  apiFunction: (params: any) => Promise<{ data: { rows: T[]; total: number } }>,
  initialParams: any = {}
) {
  const [state, setState] = useState<ApiState<{ rows: T[]; total: number }>>({
    data: { rows: [], total: 0 },
    loading: false,
    error: null,
  });

  const [pagination, setPagination] = useState({
    pageNum: 1,
    pageSize: 10,
    total: 0,
  });

  const [filters, setFilters] = useState(initialParams);
  const executeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastExecuteTimeRef = useRef<number>(0);

  const execute = useCallback(async (newParams?: any) => {
    // 防抖机制：如果距离上次执行不到500ms，则取消执行
    const now = Date.now();
    if (now - lastExecuteTimeRef.current < 500) {
      if (executeTimeoutRef.current) {
        clearTimeout(executeTimeoutRef.current);
      }
      executeTimeoutRef.current = setTimeout(() => {
        execute(newParams);
      }, 500);
      return;
    }
    
    lastExecuteTimeRef.current = now;
    const requestParams = { ...filters, ...newParams };
    setFilters(requestParams);
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const result = await apiFunction(requestParams);
      const { rows, total } = result.data;
      
      setState({ data: { rows, total }, loading: false, error: null });
      setPagination(prev => ({ ...prev, total }));
      
      return result;
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : String(error) || '请求失败';
      setState({ data: { rows: [], total: 0 }, loading: false, error: errorMessage });
      throw error;
    }
  }, [apiFunction, filters]); // 恢复filters依赖，但添加防抖机制

  const refresh = useCallback(() => {
    execute();
  }, [execute]);

  const reset = useCallback(() => {
    setState({ data: { rows: [], total: 0 }, loading: false, error: null });
    setPagination({ pageNum: 1, pageSize: 10, total: 0 });
    setFilters(initialParams);
  }, [initialParams]);

  const changePage = useCallback((pageNum: number) => {
    setPagination(prev => ({ ...prev, pageNum }));
    execute({ pageNum });
  }, [execute]);

  const changePageSize = useCallback((pageSize: number) => {
    setPagination(prev => ({ ...prev, pageSize, pageNum: 1 }));
    execute({ pageSize, pageNum: 1 });
  }, [execute]);

  const setParams = useCallback((newParams: any) => {
    const updatedFilters = { ...filters, ...newParams };
    setFilters(updatedFilters);
    execute(updatedFilters);
  }, [execute, filters]);

  return {
    ...state,
    execute,
    refresh,
    reset,
    pagination,
    filters,
    setFilters,
    setParams,
    changePage,
    changePageSize,
  };
}

/**
 * 详情API调用钩子
 */
export function useDetailApi<T = any>(
  apiFunction: (id: number) => Promise<{ data: T }>
) {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(async (id: number) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const result = await apiFunction(id);
      setState({ data: result.data, loading: false, error: null });
      return result;
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : String(error) || '请求失败';
      setState({ data: null, loading: false, error: errorMessage });
      throw error;
    }
  }, [apiFunction]);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return {
    ...state,
    execute,
    reset,
  };
}

/**
 * 操作API调用钩子（创建、更新、删除）
 */
export function useMutationApi<T = any>(
  apiFunction: (...args: any[]) => Promise<T>
) {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(async (...args: any[]) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const result = await apiFunction(...args);
      setState({ data: result, loading: false, error: null });
      return result;
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : String(error) || '操作失败';
      setState({ data: null, loading: false, error: errorMessage });
      throw error;
    }
  }, [apiFunction]);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return {
    ...state,
    execute,
    reset,
  };
}
